/**
 * UserTable Component
 * Display users in a sortable, searchable table
 */

import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Box,
  Typography,
  Tooltip,
  Skeleton,
  Chip,
  Stack,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LinkOff as DisconnectIcon,
  VpnKey as ResetMcpIcon,
} from '@mui/icons-material';
import UserStatusBadge from './UserStatusBadge';
import { format } from 'date-fns';

interface User {
  id: string;
  email: string;
  full_name: string | null;
  is_active: boolean;
  is_admin: boolean;
  email_verified: boolean;
  created_at: string;
  last_login_at: string | null;
  login_count: number;
  connection_count: number;
}

interface UserTableProps {
  users: User[];
  loading: boolean;
  page: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onViewUser: (user: User) => void;
  onEditUser: (user: User) => void;
  onDeleteUser: (user: User) => void;
  onResetMcp: (user: User) => void;
}

const UserTable: React.FC<UserTableProps> = ({
  users,
  loading,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  onViewUser,
  onEditUser,
  onDeleteUser,
  onResetMcp,
}) => {
  const formatDate = (date: string | null) => {
    if (!date) return 'Never';
    return format(new Date(date), 'MMM d, yyyy');
  };

  const formatDateTime = (date: string | null) => {
    if (!date) return 'Never';
    return format(new Date(date), 'MMM d, yyyy HH:mm');
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    onRowsPerPageChange(parseInt(event.target.value, 10));
  };

  if (loading) {
    return (
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Full Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Registered</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Connections</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...Array(5)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Full Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Registered</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell align="center">Services</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow 
                key={user.id}
                sx={{ 
                  '&:hover': { backgroundColor: 'action.hover' },
                  opacity: user.is_active ? 1 : 0.7,
                }}
              >
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {user.email}
                    </Typography>
                    {user.is_admin && (
                      <UserStatusBadge type="admin" value={true} size="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {user.full_name || '-'}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <UserStatusBadge type="active" value={user.is_active} />
                    {!user.email_verified && (
                      <UserStatusBadge type="verified" value={false} />
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Tooltip title={formatDateTime(user.created_at)}>
                    <span>{formatDate(user.created_at)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {formatDate(user.last_login_at)}
                    </Typography>
                    {user.login_count > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {user.login_count} logins
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Chip 
                    label={user.connection_count} 
                    size="small"
                    color={user.connection_count > 0 ? 'primary' : 'default'}
                    variant={user.connection_count > 0 ? 'filled' : 'outlined'}
                  />
                </TableCell>
                <TableCell align="right">
                  <Box>
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small" 
                        onClick={() => onViewUser(user)}
                        color="primary"
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit User">
                      <IconButton 
                        size="small" 
                        onClick={() => onEditUser(user)}
                        color="default"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset MCP Token">
                      <IconButton 
                        size="small" 
                        onClick={() => onResetMcp(user)}
                        color="warning"
                      >
                        <ResetMcpIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete User">
                      <IconButton 
                        size="small" 
                        onClick={() => onDeleteUser(user)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No users found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default UserTable;